'use client';

import { useState, useEffect, useCallback } from 'react';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { Trophy, TrendingUp, Users, BarChart2, UserPlus, UserCheck, ExternalLink, Target, Swords, Zap, Flame, Star, ShieldCheck, Info, X } from 'lucide-react';
import Navbar from '@/components/Navbar';
import PageSidebar, { type PageSidebarItem } from '@/components/PageSidebar';
import { useAuth } from '@/contexts/AuthContext';
import VerifiedBadge, { isVerifiedTier } from '@/components/common/VerifiedBadge';
import { useLang } from '@/lib/i18n/LanguageContext';


interface Trader {
  userId: string;
  username: string;
  displayName: string;
  bio: string | null;
  avatarUrl: string | null;
  initial: string;
  winRate: number;
  wins: number;
  losses: number;
  be: number;
  totalTrades: number;
  avgRR: number;
  performanceScore: number;
  consistencyScore: number;
  followers: number;
  isFollowing: boolean;
  xp?: number;
  level?: number;
  streakDays?: number;
  // Simulated engine metrics
  stmScore?: number;
  tier?: string | null;
  expectancyR?: number | null;
  profitFactor?: number | null;
  maxDrawdownR?: number | null;
  maxDrawdownPct?: number | null;
  eligibleForTop?: boolean;
  flags?: string[];
  equity?: number | null;
  // Live-Verified (real account)
  verificationTier?: string;
  verifiedScore?: number;
  verifiedMetrics?: any;
}

const FLAG_STYLE: Record<string, { bg: string; color: string }> = {
  HIGH_RISK: { bg: 'var(--win-muted)', color: 'var(--win)' },
  BLOWN: { bg: 'var(--bearish-muted)', color: 'var(--bearish)' },
};

type Period = '7d' | '30d' | '90d' | 'all';
type Category = 'performance' | 'verified' | 'winRate' | 'totalTrades' | 'followers' | 'consistent' | 'battles' | 'xp' | 'level' | 'streak';

const PERIODS: { label: string; value: Period }[] = [
  { label: '7 Days', value: '7d' },
  { label: '30 Days', value: '30d' },
  { label: '90 Days', value: '90d' },
  { label: 'All Time', value: 'all' },
];

const CATEGORIES: { label: string; value: Category; icon: React.ReactNode; desc: string }[] = [
  { label: 'STM Score', value: 'performance', icon: <Trophy size={14} />, desc: 'Ranked by simulated STM score' },
  { label: 'Live-Verified', value: 'verified', icon: <ShieldCheck size={14} />, desc: 'Real connected accounts (beta)' },
  { label: 'Best Win Rate', value: 'winRate', icon: <TrendingUp size={14} />, desc: 'Most accurate traders' },
  { label: 'Most Trades', value: 'totalTrades', icon: <BarChart2 size={14} />, desc: 'Most active traders' },
  { label: 'Most Followed', value: 'followers', icon: <Users size={14} />, desc: 'Most popular traders' },
  { label: 'Most Consistent', value: 'consistent', icon: <Target size={14} />, desc: 'Steady performers' },
  { label: 'Top Battlers', value: 'battles', icon: <Swords size={14} />, desc: 'Best battle win rate' },
  { label: 'Top XP', value: 'xp', icon: <Zap size={14} />, desc: 'Most XP earned' },
  { label: 'Highest Level', value: 'level', icon: <Star size={14} />, desc: 'Highest trader level' },
  { label: 'Longest Streak', value: 'streak', icon: <Flame size={14} />, desc: 'Longest active streak' },
];

// Rank #1 = gold (var(--win)); #2-3 = muted text — matches the "Top Traders This Week"
// widget pattern established in home-feed/components/TrendingSidebar.tsx.
const RANK_COLORS = ['var(--win)', 'var(--text-muted)', 'var(--text-muted)'];
const RANK_LABELS = ['🥇', '🥈', '🥉'];

// Solid brand-palette rotation for avatar fallbacks — same set as TrendingSidebar.AVATAR_COLORS.
const AVATAR_COLORS = ['var(--accent)', 'var(--info)', 'var(--bearish)', 'var(--win)', 'var(--bullish)'];

function getRankBadge(score: number, winRate: number, totalTrades: number, closedTrades?: number): string | null {
  const closed = closedTrades ?? totalTrades;
  // Elite: 15+ closed trades with 55%+ win rate
  if (closed >= 15 && winRate >= 55) return 'Elite';
  // Verified: 5–14 closed trades
  if (closed >= 5 && closed < 15) return 'Verified';
  // Rising: 1–4 closed trades
  if (closed >= 1 && closed < 5) return 'Rising';
  return null;
}

const BADGE_TOOLTIPS: Record<string, string> = {
  Elite: '15+ closed trades with a minimum 55% win rate',
  Verified: '5–14 closed trades',
  Rising: '1–4 closed trades',
};

function getBadgeStyle(badge: string): { bg: string; color: string; border: string } {
  if (badge === 'Elite') return { bg: 'var(--win-muted)', color: 'var(--win)', border: 'var(--win)' };
  if (badge === 'Pro') return { bg: 'var(--accent-muted)', color: 'var(--accent)', border: 'var(--accent)' };
  if (badge === 'Rising') return { bg: 'var(--info-muted)', color: 'var(--info)', border: 'var(--info)' };
  return { bg: 'var(--bullish-muted)', color: 'var(--bullish)', border: 'var(--bullish)' };
}

// Table grid: # · Trader · Win Rate · Trades · Avg R · Followers · Follow — per the
// layout-unifikacija Trejderi mockup. Fixed widths for the numeric columns, flexible for Trader.
const TABLE_GRID = '52px 1fr 90px 90px 70px 100px 110px';

export default function LeaderboardPage() {
  const { t } = useLang();
  const router = useRouter();
  const { user: currentUser } = useAuth();
  const [traders, setTraders] = useState<Trader[]>([]);
  // Listed but not ranked: fewer than `minTrades` closed trades in the period (see lib/leaderboard/config).
  const [unranked, setUnranked] = useState<Trader[]>([]);
  const [minTrades, setMinTrades] = useState(0);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState<Period>('30d');
  const [category, setCategory] = useState<Category>('performance');
  const [followLoading, setFollowLoading] = useState<string | null>(null);
  const [showScoring, setShowScoring] = useState(false); // "How scoring works" explainer (display-only)

  // Dismiss the explainer on Escape.
  useEffect(() => {
    if (!showScoring) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setShowScoring(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [showScoring]);

  const fetchLeaderboard = useCallback(async () => {
    setLoading(true);
    try {
      if (category === 'battles') {
        const res = await fetch('/api/battles?section=winners');
        const data = await res.json();
        // Map battle winners to Trader shape
        const mapped = (data.winners || []).map((w: any) => ({
          userId: w.userId,
          username: w.username,
          displayName: w.displayName,
          bio: null,
          avatarUrl: w.avatarUrl,
          initial: w.initial,
          winRate: w.battleWinRate,
          wins: w.battlesWon,
          losses: w.battlesLost,
          be: 0,
          totalTrades: w.battlesWon + w.battlesLost,
          avgRR: 0,
          performanceScore: w.performanceScore,
          consistencyScore: 0,
          followers: 0,
          isFollowing: false,
        }));
        setTraders(mapped);
        setUnranked([]);
        setMinTrades(0);
      } else {
        const res = await fetch(`/api/leaderboard?period=${period}&category=${category}`);
        const data = await res.json();
        setTraders(data.traders ?? []);
        setUnranked(data.unranked ?? []);
        setMinTrades(Number(data.minTrades ?? 0));
      }
    } catch {
      setTraders([]);
      setUnranked([]);
    } finally {
      setLoading(false);
    }
  }, [period, category]);

  useEffect(() => {
    fetchLeaderboard();
  }, [fetchLeaderboard]);

  const handleFollow = async (trader: Trader) => {
    if (!currentUser) return;
    setFollowLoading(trader.userId);
    try {
      const res = await fetch(`/api/users/${encodeURIComponent(trader.username)}/follow`, {
        method: 'POST',
        credentials: 'include',
      });
      if (res.ok) {
        const data = await res.json();
        setTraders((prev) =>
          prev.map((t) =>
            t.userId === trader.userId
              ? { ...t, isFollowing: data.following, followers: data.followerCount }
              : t
          )
        );
      }
    } catch {
      // silent
    } finally {
      setFollowLoading(null);
    }
  };

  const top3 = traders.slice(0, 3);

  // Left "TREJDERI" sidebar — 5 curated shortcuts onto the existing period/category state.
  // Mapping (no new API params invented):
  //   Rang lista        -> category='performance' (default STM ranking), and not the 7d quick-filter
  //   Top nedelje        -> period='7d' (quick "this week" cut of whatever category is active)
  //   Najpraćeniji       -> category='followers'
  //   Signal provajderi  -> category='totalTrades' (closest existing stat to "most active posters")
  //   Verifikovani       -> category='verified' (Live-Verified real-account ranking)
  const sidebarItems: PageSidebarItem[] = [
    {
      key: 'rankings',
      emoji: '🏆',
      label: t('Rankings'),
      active: category === 'performance' && period !== '7d',
      onClick: () => setCategory('performance'),
    },
    {
      key: 'top-week',
      emoji: '📅',
      label: t('Top this week'),
      active: period === '7d',
      onClick: () => setPeriod('7d'),
    },
    {
      key: 'most-followed',
      emoji: '👥',
      label: t('Most Followed'),
      active: category === 'followers',
      onClick: () => setCategory('followers'),
    },
    {
      key: 'signal-providers',
      emoji: '⚡',
      label: t('Signal Providers'),
      active: category === 'totalTrades',
      onClick: () => setCategory('totalTrades'),
    },
    {
      key: 'verified',
      emoji: '✅',
      label: t('Verified Traders'),
      active: category === 'verified',
      onClick: () => setCategory('verified'),
    },
  ];

  return (
    <div className="min-h-screen" style={{ background: 'var(--bg-base)' }}>
      <Navbar />

      <div className="mx-auto px-4 lg:px-8 py-6 pb-28 md:pb-10" style={{ maxWidth: 1280 }}>
        <div className="flex gap-0 md:gap-2 items-start">
          <PageSidebar
            title={t('Traders')}
            items={sidebarItems}
            infoTitle={t('HOW WE RANK')}
            infoText={t('Rankings are calculated exclusively from verified trades: win rate, average R, and consistency. No self-reported results.')}
          />

          <main className="flex-1 min-w-0 py-6">
            {/* Header */}
            <div className="flex items-start gap-3 mb-5">
              <div>
                <div className="flex items-center gap-2 flex-wrap">
                  <h1 className="font-heading" style={{ fontSize: 26, fontWeight: 800, color: 'var(--text-primary)' }}>{t('Traders')}</h1>
                  {category === 'verified' ? (
                    <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full" style={{ background: 'var(--bullish-muted)', color: 'var(--bullish)', border: '1px solid transparent' }}>
                      {t('LIVE · BETA')}
                    </span>
                  ) : (
                    <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full" style={{ background: 'var(--win-muted)', color: 'var(--win)', border: '1px solid transparent' }}>
                      {t('SIMULATED')}
                    </span>
                  )}
                </div>
                <p style={{ fontSize: 14.5, color: 'var(--text-secondary)' }}>
                  {category === 'verified'
                    ? t('Real connected accounts (read-only), ranked by verified score — beta')
                    : t('Ranked by STM score from a $10k simulated account — not real money')}
                </p>
                <button
                  type="button"
                  onClick={() => setShowScoring(true)}
                  className="mt-1.5 inline-flex items-center gap-1 text-xs font-medium transition-colors duration-100"
                  style={{ color: 'var(--accent)' }}
                  onMouseEnter={(e) => (e.currentTarget.style.color = 'var(--accent-hover)')}
                  onMouseLeave={(e) => (e.currentTarget.style.color = 'var(--accent)')}
                >
                  <Info size={13} /> {t('How scoring works')}
                </button>
              </div>
            </div>

            {/* "How scoring works" explainer (display-only — math lives in settle-trades/analyzeTrader) */}
            {showScoring && (
              <div
                className="fixed inset-0 z-[80] flex items-center justify-center p-4"
                style={{ background: 'rgba(0,0,0,0.7)' }}
                onClick={() => setShowScoring(false)}
                role="dialog"
                aria-modal="true"
                aria-label={t('How the STM Score works')}
              >
                <div
                  className="relative w-full max-w-lg max-h-[85vh] overflow-y-auto rounded-2xl p-5 sm:p-6 animate-fade-in"
                  style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', boxShadow: '0 20px 60px rgba(28,43,38,0.25)' }}
                  onClick={(e) => e.stopPropagation()}
                >
                  <button
                    onClick={() => setShowScoring(false)}
                    aria-label={t('Close')}
                    className="absolute top-3 right-3 p-1.5 rounded-lg transition-colors duration-100"
                    style={{ color: 'var(--text-muted)', background: 'var(--bg-sunken)' }}
                  >
                    <X size={16} />
                  </button>
                  <h2 className="font-heading text-lg font-bold pr-8 mb-3" style={{ color: 'var(--text-primary)' }}>{t('How the STM Score works')}</h2>
                  <div className="space-y-4 text-sm leading-relaxed" style={{ color: 'var(--text-body)' }}>
                    <p>
                      {t('Your')} <strong style={{ color: 'var(--text-primary)' }}>{t('STM Score (0–100)')}</strong> {t('measures trading skill on a')}{' '}
                      <strong style={{ color: 'var(--text-primary)' }}>{t('simulated $10,000 account — not real money')}</strong>
                      {t(". It rewards a consistent, risk-controlled edge, and it's built so it can't be faked with luck or big gambles.")}
                    </p>
                    <div>
                      <p className="font-semibold mb-1.5" style={{ color: 'var(--bullish)' }}>{t('Your score goes up when you:')}</p>
                      <ul className="list-disc pl-5 space-y-1">
                        <li>{t('Make money consistently across many trades — a real, proven edge, not one lucky win')}</li>
                        <li>{t('Keep winners bigger than losers (profit factor above 1)')}</li>
                        <li>{t('Grow smoothly, with small drawdowns')}</li>
                        <li>{t('Risk a small, steady amount per trade (around 1%, and never more than a few %)')}</li>
                      </ul>
                    </div>
                    <div>
                      <p className="font-semibold mb-1.5" style={{ color: 'var(--bearish)' }}>{t('Your score stays low — or 0 — when you:')}</p>
                      <ul className="list-disc pl-5 space-y-1">
                        <li>{t('Have too few trades: the score only reaches full strength after about')} <strong style={{ color: 'var(--text-primary)' }}>{t('50 trades over ~8 weeks')}</strong></li>
                        <li>{t('Take oversized risk or run big drawdowns')}</li>
                        <li>{t('Blow up the account (score resets to 0)')}</li>
                      </ul>
                    </div>
                    <p>
                      <strong style={{ color: 'var(--text-primary)' }}>{t('To qualify for the ranked Top you need:')}</strong>{' '}
                      {t('30+ settled trades over 6+ weeks, a proven positive edge, profit factor ≥ 1.2, max risk ≤ 5% per trade, drawdown under 40%, and no blow-ups.')}
                    </p>
                    <p>
                      <strong style={{ color: 'var(--text-primary)' }}>{t('Tiers:')}</strong>{' '}
                      {t('Rising → Verified → Elite, as your track record gets longer and cleaner.')}
                    </p>
                    <p className="text-xs italic" style={{ color: 'var(--text-muted)' }}>
                      {t('Other tabs rank by a single stat (best win rate, most trades, most followed, most consistent, top battles).')}{' '}
                      <strong style={{ color: 'var(--text-secondary)' }}>Live-Verified</strong> {t('ranks traders by real-account results instead of the simulated score.')}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Category tabs */}
            <div className="flex gap-2 mb-3 overflow-x-auto pb-1">
              {CATEGORIES.map((c) => (
                <button
                  key={c.value}
                  onClick={() => setCategory(c.value)}
                  className="flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium shrink-0 transition-all duration-150"
                  style={{
                    background: category === c.value ? 'var(--accent-muted)' : 'var(--bg-card)',
                    color: category === c.value ? 'var(--accent)' : 'var(--text-secondary)',
                    border: category === c.value ? '1px solid var(--accent)' : '1px solid var(--border)',
                  }}
                >
                  <span style={{ color: category === c.value ? 'var(--accent)' : 'var(--text-muted)' }}>{c.icon}</span>
                  {t(c.label)}
                </button>
              ))}
            </div>

            {/* Period filters — "Ukupno / Ovog meseca / Ove nedelje" pill styling */}
            <div className="flex gap-2 mb-6 overflow-x-auto pb-1">
              {PERIODS.map((p) => (
                <button
                  key={p.value}
                  onClick={() => setPeriod(p.value)}
                  className="px-4 py-2 rounded-full text-sm font-semibold shrink-0 transition-all duration-150"
                  style={{
                    background: period === p.value ? 'var(--accent-hover)' : 'var(--bg-card)',
                    color: period === p.value ? '#ffffff' : 'var(--text-secondary)',
                    border: period === p.value ? '1px solid var(--accent-hover)' : '1px solid var(--border)',
                  }}
                >
                  {t(p.label)}
                </button>
              ))}
            </div>

            {/* Top 3 podium */}
            {!loading && top3.length >= 3 && (
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-6">
                {[top3[1], top3[0], top3[2]].map((trader, podiumIdx) => {
                  const rank = podiumIdx === 1 ? 0 : podiumIdx === 0 ? 1 : 2;
                  const isFirst = podiumIdx === 1;
                  const badge = getRankBadge(trader.performanceScore, trader.winRate, trader.totalTrades);
                  return (
                    <div
                      key={trader.userId}
                      role="button"
                      tabIndex={0}
                      onClick={() => router.push(`/profile/${encodeURIComponent(trader.username)}`)}
                      onKeyDown={(e) => { if (e.key === 'Enter') router.push(`/profile/${encodeURIComponent(trader.username)}`); }}
                      className="rounded-2xl p-4 text-center flex flex-col items-center gap-2 cursor-pointer transition-transform duration-100 hover:scale-[1.02]"
                      style={{
                        background: isFirst ? 'var(--win-muted)' : 'var(--bg-card)',
                        border: `1.5px solid ${isFirst ? 'var(--win)' : 'var(--border)'}`,
                        boxShadow: isFirst ? '0 0 0 3px var(--win-glow)' : 'none',
                      }}
                    >
                      <span className="text-2xl">{RANK_LABELS[rank]}</span>
                      <div className="relative">
                        <div
                          className="w-12 h-12 rounded-full flex items-center justify-center text-lg font-bold overflow-hidden"
                          style={{
                            background: trader.avatarUrl ? undefined : AVATAR_COLORS[rank % AVATAR_COLORS.length],
                            color: 'var(--bg-base)',
                          }}
                        >
                          {trader.avatarUrl ? <img src={trader.avatarUrl} alt={trader.username} className="w-full h-full object-cover" /> : trader.initial}
                        </div>
                        {isVerifiedTier(trader.verificationTier) && <VerifiedBadge size={16} />}
                      </div>
                      <div>
                        <p className="text-sm font-semibold max-w-[180px]" style={{ color: 'var(--text-primary)' }}>@{trader.username}</p>
                        {badge && (
                          <span
                            className="inline-block text-xs px-1.5 py-0.5 rounded font-semibold mt-0.5 cursor-help"
                            style={{ background: getBadgeStyle(badge).bg, color: getBadgeStyle(badge).color, border: `1px solid ${getBadgeStyle(badge).border}` }}
                            title={t(BADGE_TOOLTIPS[badge])}
                          >
                            {badge}
                          </span>
                        )}
                        <p className="font-mono-nums text-lg font-bold mt-1" style={{ color: RANK_COLORS[rank] }}>
                          {category === 'performance' ? `${(trader.stmScore ?? 0).toFixed(0)}` :
                           category === 'verified' ? `${(trader.verifiedScore ?? 0).toFixed(0)}` :
                           category === 'winRate' ? `${trader.winRate}%` :
                           category === 'totalTrades' ? trader.totalTrades :
                           category === 'followers' ? trader.followers :
                           `${trader.consistencyScore}%`}
                        </p>
                        <p className="text-xs" style={{ color: 'var(--text-muted)' }}>
                          {category === 'performance' ? (trader.tier ? String(trader.tier).charAt(0) + String(trader.tier).slice(1).toLowerCase() : t('STM score')) :
                           category === 'verified' ? t('verified · live') :
                           category === 'winRate' ? `${trader.totalTrades} ${t('trades')}` :
                           category === 'totalTrades' ? t('trades') :
                           category === 'followers'? t('followers') : t('consistency')}
                        </p>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {/* Ranking table */}
            <div className="rounded-2xl overflow-x-auto" style={{ border: '1px solid var(--border)', background: 'var(--bg-card)' }}>
              {/* Table header */}
              <div
                className="grid items-center px-5 py-3 text-xs font-semibold uppercase tracking-wide"
                style={{ color: 'var(--text-muted)', borderBottom: '1px solid var(--border)', gridTemplateColumns: TABLE_GRID, gap: '8px' }}
              >
                <span>#</span>
                <span>{t('Trader')}</span>
                <span className="text-right">{t('Win %')}</span>
                <span className="text-right">{t('Trades')}</span>
                <span className="text-right">{t('Avg RR')}</span>
                <span className="text-right">{t('followers')}</span>
                <span className="text-right">{t('Actions')}</span>
              </div>

              {loading ? (
                <div className="divide-y" style={{ borderColor: 'var(--border)' }}>
                  {Array.from({ length: 8 }).map((_, i) => (
                    <div key={i} className="grid items-center px-5 py-4" style={{ gridTemplateColumns: TABLE_GRID, gap: '8px' }}>
                      <div className="w-6 h-4 rounded skeleton" />
                      <div className="flex items-center gap-3">
                        <div className="w-9 h-9 rounded-full skeleton" />
                        <div className="space-y-1.5">
                          <div className="w-24 h-3 rounded skeleton" />
                          <div className="w-16 h-2.5 rounded skeleton" />
                        </div>
                      </div>
                      <div className="w-10 h-4 rounded skeleton ml-auto" />
                      <div className="w-10 h-4 rounded skeleton ml-auto" />
                      <div className="w-10 h-4 rounded skeleton ml-auto" />
                      <div className="w-10 h-4 rounded skeleton ml-auto" />
                      <div className="w-20 h-8 rounded-full skeleton ml-auto" />
                    </div>
                  ))}
                </div>
              ) : traders.length === 0 ? (
                <div className="py-16 text-center">
                  <Trophy size={40} className="mx-auto mb-3" style={{ color: 'var(--text-dim)' }} />
                  <p className="text-sm font-medium" style={{ color: 'var(--text-secondary)' }}>
                    {category === 'verified' ? t('No live-verified traders yet')
                      : unranked.length > 0 ? t('No one has enough closed trades in this period yet.')
                      : t('No traders yet')}
                  </p>
                  <p className="text-xs mt-1" style={{ color: 'var(--text-muted)', maxWidth: 360, margin: '4px auto 0' }}>
                    {category === 'verified'
                      ? t('Connect a real account (Verify account) — pass the gates (≥30 trades, ≥6 weeks, DD≤40%, PF≥1.2) to appear here.')
                      : unranked.length > 0
                        ? <>{t('Ranking starts at')} {minTrades} {t('closed trades — traders who are close are listed below.')}</>
                        : t('Post a signal to show up on the leaderboard.')}
                  </p>
                </div>
              ) : (
                <div className="divide-y" style={{ borderColor: 'var(--border-subtle)' }}>
                  {traders.map((trader, idx) => {
                    const badge = getRankBadge(trader.performanceScore, trader.winRate, trader.totalTrades);
                    const isTop3 = idx < 3;
                    return (
                      <div
                        key={trader.userId}
                        role="button"
                        tabIndex={0}
                        onClick={() => router.push(`/profile/${encodeURIComponent(trader.username)}`)}
                        onKeyDown={(e) => { if (e.key === 'Enter') router.push(`/profile/${encodeURIComponent(trader.username)}`); }}
                        className="grid items-center px-5 py-3.5 transition-colors duration-100 cursor-pointer"
                        style={{ gridTemplateColumns: TABLE_GRID, gap: '8px' }}
                        onMouseEnter={(e) => ((e.currentTarget as HTMLDivElement).style.background = 'var(--bg-card-hover)')}
                        onMouseLeave={(e) => ((e.currentTarget as HTMLDivElement).style.background = 'transparent')}
                      >
                        {/* Rank */}
                        <span
                          className="font-mono-nums text-center"
                          style={{ fontSize: 15, fontWeight: 800, color: isTop3 ? RANK_COLORS[idx] : 'var(--text-dim)' }}
                        >
                          {isTop3 ? RANK_LABELS[idx] : idx + 1}
                        </span>

                        {/* Trader info */}
                        <div className="flex items-center gap-3 min-w-0">
                          <div className="relative shrink-0">
                            <div
                              className="w-9 h-9 rounded-full flex items-center justify-center text-sm font-bold overflow-hidden"
                              style={{ background: trader.avatarUrl ? undefined : AVATAR_COLORS[idx % AVATAR_COLORS.length], color: 'var(--bg-base)' }}
                            >
                              {trader.avatarUrl ? <img src={trader.avatarUrl} alt={trader.username} className="w-full h-full object-cover" /> : trader.initial}
                            </div>
                            {isVerifiedTier(trader.verificationTier) && <VerifiedBadge size={14} />}
                          </div>
                          <div className="min-w-0">
                            <div className="flex items-center gap-1.5 flex-wrap">
                              <Link href={`/profile/${trader.username}`} className="text-sm font-extrabold truncate hover:underline" style={{ color: 'var(--text-primary)' }}>
                                {trader.displayName || trader.username}
                              </Link>
                              {badge && (
                                <span
                                  className="text-xs px-1.5 py-0.5 rounded font-semibold shrink-0 cursor-help"
                                  style={{ background: getBadgeStyle(badge).bg, color: getBadgeStyle(badge).color, border: `1px solid ${getBadgeStyle(badge).border}` }}
                                  title={t(BADGE_TOOLTIPS[badge])}
                                >
                                  {badge}
                                </span>
                              )}
                              {(trader.flags ?? []).map((f) => (
                                <span key={f} className="text-[10px] px-1.5 py-0.5 rounded font-semibold shrink-0 cursor-help"
                                  title={f === 'HIGH_RISK' ? t('Over-risking (max risk > 5% or avg > 3%)') : f === 'BLOWN' ? t('Simulated account was wiped') : f}
                                  style={{ background: FLAG_STYLE[f]?.bg ?? 'var(--bg-sunken)', color: FLAG_STYLE[f]?.color ?? 'var(--text-secondary)' }}>
                                  {t(f.replace('_', ' '))}
                                </span>
                              ))}
                            </div>
                            <p className="text-xs truncate" style={{ color: 'var(--text-muted)' }}>@{trader.username}</p>
                          </div>
                        </div>

                        {/* Win Rate */}
                        <div className="text-right">
                          <span className="font-mono-nums" style={{ fontSize: 14, fontWeight: 800, color: 'var(--win)' }}>
                            {category === 'battles' ? `${trader.wins}W` : `${trader.winRate}%`}
                          </span>
                        </div>

                        {/* Total Trades */}
                        <span className="font-mono-nums text-sm text-right" style={{ color: 'var(--text-secondary)' }}>{trader.totalTrades}</span>

                        {/* Avg RR */}
                        <span className="font-mono-nums text-right" style={{ fontSize: 14, fontWeight: 800, color: 'var(--accent-bright)' }}>
                          {trader.avgRR > 0 ? `${trader.avgRR.toFixed(1)}R` : '—'}
                        </span>

                        {/* Followers */}
                        <span className="font-mono-nums text-sm text-right" style={{ color: 'var(--text-secondary)' }}>{trader.followers.toLocaleString()}</span>

                        {/* Actions */}
                        <div className="flex items-center justify-end gap-1.5">
                          <Link
                            href={`/profile/${trader.username}`}
                            className="p-1.5 rounded-lg transition-colors duration-100"
                            style={{ color: 'var(--text-muted)' }}
                            title={t('View profile')}
                            onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--bg-sunken)'; e.currentTarget.style.color = 'var(--text-primary)'; }}
                            onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = 'var(--text-muted)'; }}
                          >
                            <ExternalLink size={14} />
                          </Link>
                          <button
                            onClick={(e) => { e.stopPropagation(); handleFollow(trader); }}
                            disabled={followLoading === trader.userId || !currentUser}
                            className="flex items-center gap-1.5 px-3.5 py-1.5 rounded-full text-xs font-semibold transition-all duration-150"
                            style={{
                              background: trader.isFollowing ? 'var(--bg-sunken)' : 'transparent',
                              color: trader.isFollowing ? 'var(--text-secondary)' : 'var(--accent-bright)',
                              border: trader.isFollowing ? '1px solid var(--border)' : '1px solid var(--accent-bright)',
                              opacity: followLoading === trader.userId ? 0.6 : 1,
                            }}
                          >
                            {trader.isFollowing ? <><UserCheck size={12} /><span className="hidden sm:inline">{t('Following')}</span></> : <><UserPlus size={12} /><span className="hidden sm:inline">{t('Follow')}</span></>}
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Listed, not ranked: too few closed trades in this period. No rank numbers, never on the podium. */}
            {!loading && unranked.length > 0 && (
              <section className="mt-6" aria-label={t('Not enough trades yet')}>
                <h2 className="font-heading" style={{ fontSize: 15, fontWeight: 800, color: 'var(--text-primary)' }}>
                  {t('Not enough trades yet')}
                </h2>
                <p className="text-xs mt-0.5 mb-3" style={{ color: 'var(--text-muted)' }}>
                  {t('Ranking starts at')} {minTrades} {t('closed trades in the selected period.')}
                </p>
                <div className="flex flex-wrap gap-2">
                  {unranked.map((trader, idx) => (
                    <Link
                      key={trader.userId}
                      href={`/profile/${encodeURIComponent(trader.username)}`}
                      className="flex items-center gap-2 pl-1 pr-3 py-1 rounded-full text-xs transition-colors duration-100"
                      style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', color: 'var(--text-secondary)' }}
                      title={`${trader.totalTrades}/${minTrades} ${t('closed trades')}`}
                    >
                      <span
                        className="w-6 h-6 rounded-full flex items-center justify-center text-[11px] font-bold overflow-hidden shrink-0"
                        style={{ background: trader.avatarUrl ? undefined : AVATAR_COLORS[idx % AVATAR_COLORS.length], color: 'var(--bg-base)' }}
                      >
                        {trader.avatarUrl ? <img src={trader.avatarUrl} alt="" className="w-full h-full object-cover" /> : trader.initial}
                      </span>
                      <span className="font-semibold" style={{ color: 'var(--text-primary)' }}>@{trader.username}</span>
                      <span className="font-mono-nums" style={{ color: 'var(--text-muted)' }}>{trader.totalTrades}/{minTrades}</span>
                    </Link>
                  ))}
                </div>
              </section>
            )}

            {!loading && traders.length > 0 && (
              <p className="text-center text-xs mt-4" style={{ color: 'var(--text-muted)' }}>
                {t('Traders shown:')} {traders.length} · {t(PERIODS.find((p) => p.value === period)?.label ?? '')} · {t(CATEGORIES.find((c) => c.value === category)?.desc ?? '')}
              </p>
            )}
          </main>
        </div>
      </div>
    </div>
  );
}
